import { activityLogApi } from './activity-log';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Parse error response from the API
 * @param {Response} response - The fetch response object
 * @returns {Promise<string>} Error message
 */
const parseErrorResponse = async (response) => {
  try {
    const errorData = await response.json();
    
    // Handle FastAPI HTTPException format
    if (errorData.detail) {
      if (typeof errorData.detail === 'string') {
        return errorData.detail;
      }
      if (Array.isArray(errorData.detail)) {
        return errorData.detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
      }
      return JSON.stringify(errorData.detail);
    }
    
    return errorData.message || `HTTP error! status: ${response.status}`;
  } catch (parseError) {
    return `HTTP error! status: ${response.status}`;
  }
};

/**
 * Make authenticated request with JWT token
 * @param {string} url - The API endpoint URL
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
const makeAuthenticatedRequest = async (url, options = {}) => {
  const token = localStorage.getItem('access_token');
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });
};

export const dealsApi = {
  /**
   * Fetch all deals from the backend API
   * @returns {Promise<Array>} Array of deal objects
   */
  getAllDeals: async () => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/v1/deals/`);
      
      if (!response.ok) {
        const errorMessage = await parseErrorResponse(response);
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error fetching deals:', error);
      throw error;
    }
  },

  /**
   * Update a deal's stage with activity logging
   * @param {number} dealId - ID of the deal to update
   * @param {string} newStage - New stage value
   * @param {string} oldStage - Old stage value (no longer used for logging)
   * @param {string} dealName - Deal name for logging (no longer used)
   * @returns {Promise<Object>} Updated deal object
   */
  updateDealStage: async (dealId, newStage, oldStage = null, dealName = '') => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/v1/deals/${dealId}/stage`, {
        method: 'PATCH',
        body: JSON.stringify({ Stage: newStage }),
      });

      if (!response.ok) {
        const errorMessage = await parseErrorResponse(response);
        throw new Error(errorMessage);
      }

      const updatedDeal = await response.json();

      // Note: Activity logging with StageFrom and StageTo is now handled automatically by the backend
      // No need for manual activity log creation here

      return updatedDeal;
    } catch (error) {
      console.error('Error updating deal stage:', error);
      throw error;
    }
  },

  /**
   * Create a new deal with activity logging
   * @param {Object} dealData - Deal data to create
   * @returns {Promise<Object>} Created deal object
   */
  createDeal: async (dealData) => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/v1/deals/`, {
        method: 'POST',
        body: JSON.stringify(dealData),
      });

      if (!response.ok) {
        const errorMessage = await parseErrorResponse(response);
        throw new Error(errorMessage);
      }

      const createdDeal = await response.json();
      
      // Log deal creation activity
      try {
        await activityLogApi.createActivityLog(
          createdDeal.ID, 
          `created deal`
        );
      } catch (logError) {
        console.error('Error logging deal creation:', logError);
        // Don't fail the deal creation if logging fails
      }

      return createdDeal;
    } catch (error) {
      console.error('Error creating deal:', error);
      throw error;
    }
  },

  /**
   * Toggle deal display (hide/show rejected or lost deals)
   * @param {number} dealId - ID of the deal to toggle display
   * @param {boolean} displayDeal - Whether to display the deal
   * @returns {Promise<Object>} Updated deal display status
   */
  toggleDealDisplay: async (dealId, displayDeal) => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/v1/deals/${dealId}/toggle-display`, {
        method: 'PATCH',
        body: JSON.stringify({ DisplayDeal: displayDeal }),
      });

      if (!response.ok) {
        const errorMessage = await parseErrorResponse(response);
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Error toggling deal display:', error);
      throw error;
    }
  },

  /**
   * Update deal value and expected closure date with activity logging
   * @param {number} dealId - ID of the deal to update
   * @param {Object} updateData - Data to update (DealValue, ExpectedClosureDate)
   * @param {string} dealName - Deal name for logging (no longer used)
   * @param {Object} originalDeal - Original deal data for comparison
   * @returns {Promise<Object>} Updated deal object
   */
  updateDeal: async (dealId, updateData, dealName = '', originalDeal = null) => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/v1/deals/${dealId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorMessage = await parseErrorResponse(response);
        throw new Error(errorMessage);
      }

      const updatedDeal = await response.json();

      // Log deal update activity only for fields that actually changed
      try {
        const changes = [];
        
        if (updateData.DealValue !== undefined && originalDeal) {
          const oldValue = parseFloat(originalDeal.DealValue);
          const newValue = parseFloat(updateData.DealValue);
          if (oldValue !== newValue) {
            // Import formatIndianCurrency at the top of the file
            const { formatIndianCurrency } = await import('../utils/formatters');
            changes.push(`updated deal value from ${formatIndianCurrency(oldValue)} to ${formatIndianCurrency(newValue)}`);
          }
        }
        
        if (updateData.ExpectedClosureDate !== undefined && originalDeal) {
          // Format dates for comparison (YYYY-MM-DD format)
          const formatDateForComparison = (dateString) => {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toISOString().split('T')[0];
          };
          
          const oldDate = formatDateForComparison(originalDeal.ExpectedClosureDate);
          const newDate = formatDateForComparison(updateData.ExpectedClosureDate);
          
          if (oldDate !== newDate) {
            const formattedDate = new Date(updateData.ExpectedClosureDate).toLocaleDateString('en-IN');
            const formattedOldDate = new Date(originalDeal.ExpectedClosureDate).toLocaleDateString('en-IN');
            changes.push(`updated expected closure date from ${formattedOldDate} to ${formattedDate}`);
          }
        }
        
        if (changes.length > 0) {
          const action = changes.join(' and ');
          await activityLogApi.createActivityLog(dealId, action);
        }
      } catch (logError) {
        console.error('Error logging deal update:', logError);
        // Don't fail the deal update if logging fails
      }

      return updatedDeal;
    } catch (error) {
      console.error('Error updating deal:', error);
      throw error;
    }
  },

  /**
   * Update a deal's temperature with activity logging
   * @param {number} dealId - ID of the deal to update
   * @param {string} temperature - New temperature value (HOT, WARM, COLD)
   * @returns {Promise<Object>} Updated deal object
   */
  updateDealTemperature: async (dealId, temperature) => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/v1/deals/${dealId}/temperature`, {
        method: 'PATCH',
        body: JSON.stringify({ Temperature: temperature }),
      });

      if (!response.ok) {
        const errorMessage = await parseErrorResponse(response);
        throw new Error(errorMessage);
      }

      const updatedDeal = await response.json();

      return updatedDeal;
    } catch (error) {
      console.error('Error updating deal temperature:', error);
      throw error;
    }
  },

  /**
   * Get all deals for a specific salesperson
   * @param {string} ecode - Salesperson employee code
   * @returns {Promise<Array>} Array of deal objects
   */
  getDealsBySalesperson: async (ecode) => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/v1/deals/salesperson/${ecode}`);
      
      if (!response.ok) {
        const errorMessage = await parseErrorResponse(response);
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error fetching deals by salesperson:', error);
      throw error;
    }
  },

  /**
   * Reassign multiple deals to a new salesperson
   * @param {Array<number>} dealIds - Array of deal IDs to reassign
   * @param {string} newSalespersonEcode - New salesperson employee code
   * @returns {Promise<Object>} Reassignment result
   */
  reassignDeals: async (dealIds, newSalespersonEcode) => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/v1/deals/reassign-salesperson`, {
        method: 'PATCH',
        body: JSON.stringify({
          deal_ids: dealIds,
          new_salesperson_ecode: newSalespersonEcode
        }),
      });

      if (!response.ok) {
        const errorMessage = await parseErrorResponse(response);
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Error reassigning deals:', error);
      throw error;
    }
  }
};